import { MGPOptional, MGPValidation, Set } from '@everyboard/lib';
import { NumberConfig } from '../../config/NumberConfig';
import { RulesConfigDescription } from '../../config/RulesConfigDescription';
import { AlignmentStatus } from '../../jscaip/AI/AlignmentHeuristic';
import { GameNode } from '../../jscaip/AI/GameNode';
import { Coord } from '../../jscaip/Coord';
import { CoordSet } from '../../jscaip/CoordSet';
import { GameStatus } from '../../jscaip/GameStatus';
import { Player } from '../../jscaip/Player';
import { ConfigurableRules } from '../../jscaip/Rules';
import { RulesFailure } from '../../jscaip/RulesFailure';
import { TableUtils } from '../../jscaip/TableUtils';
import { Debug } from '../../utils/Debug';
import { MGPValidators } from '../../utils/MGPValidator';
import { QuartoFailure } from './QuartoFailure';
import { QuartoPiece } from './QuartoPiece';
import { QuartoState } from './QuartoState';
/**
 * A criterion is a list of boolean sub-criteria, so three possible values: true, false, null.
 * false means that we need a specific value (e.g., big), true is the opposite (e.g., small)
 * null means that this criterion has been neutralized
 * (if a pattern contains a big and a small piece, for example).
 */
class QuartoCriterion {
    subCriterion = [MGPOptional.empty(), MGPOptional.empty(), MGPOptional.empty(), MGPOptional.empty()];
    constructor(piece) {
        // a criterion is initialized with a piece, it takes the piece's value
        this.subCriterion[0] = MGPOptional.of((piece.value & 8) === 8);
        this.subCriterion[1] = MGPOptional.of((piece.value & 4) === 4);
        this.subCriterion[2] = MGPOptional.of((piece.value & 2) === 2);
        this.subCriterion[3] = MGPOptional.of((piece.value & 1) === 1);
    }
    /**
     * Merge with another criterion.
     * This will keep what both have in common
     * Returns true if at least one criterion is common, false otherwise
     */
    mergeWith(other) {
        let nonNull = 4;
        for (let i = 0; i < 4; i++) {
            if (this.subCriterion[i].equals(other.subCriterion[i]) === false) {
                /*
                 * if the piece represented by `other` is different from this piece
                 * on their ith criterion, then there is no common criterion (null)
                 */
                this.subCriterion[i] = MGPOptional.empty();
            }
            if (this.subCriterion[i].isAbsent()) {
                // if after this, the ith criterion is empty, then it lost a criterion
                nonNull--;
            }
        }
        return nonNull > 0;
    }
    mergeWithQuartoPiece(piece) {
        const criterion = new QuartoCriterion(piece);
        return this.mergeWith(criterion);
    }
    areAllAbsent() {
        for (let i = 0; i < 4; i++) {
            if (this.subCriterion[i].isPresent()) {
                return false;
            }
        }
        return true;
    }
    // returns true if there is at least one sub-criterion in common between the two
    match(c) {
        for (let i = 0; i < 4; i++) {
            if (this.subCriterion[i].equals(c.subCriterion[i])) {
                return true;
            }
        }
        return false;
    }
    matchPiece(piece) {
        return this.match(new QuartoCriterion(piece));
    }
    toString() {
        return 'Criterion{' +
            this.subCriterion.map((b) => {
                if (b.isPresent()) {
                    if (b.get()) {
                        return '1';
                    }
                    else {
                        return '0';
                    }
                }
                else {
                    return 'x';
                }
            }).join(' ') + '}';
    }
}
export class QuartoNode extends GameNode {
}
export class VictoryPattern {
    level;
    coordPattern;
    initialCoord;
    constructor(level, coordPattern, initialCoord) {
        this.level = level;
        this.coordPattern = coordPattern;
        this.initialCoord = initialCoord;
    }
    getCoords() {
        return this.coordPattern.map((element) => element.getNext(this.initialCoord));
    }
}
class VerticalVictoryPattern extends VictoryPattern {
    static VERTICAL = new CoordSet([
        new Coord(0, 0),
        new Coord(0, 1),
        new Coord(0, 2),
        new Coord(0, 3),
    ]);
    constructor(initialCoord) {
        super(1, VerticalVictoryPattern.VERTICAL, initialCoord);
    }
    getAllPatterns(state) {
        const maxY = state.getHeight() - 4;
        return state
            .getCoordsAndContents()
            .map((coordAndContent) => coordAndContent.coord)
            .filter((coord) => coord.y <= maxY)
            .map((coord) => new VerticalVictoryPattern(coord));
    }
}
class HorizontalVictoryPattern extends VictoryPattern {
    static HORIZONTAL = new CoordSet([
        new Coord(0, 0),
        new Coord(1, 0),
        new Coord(2, 0),
        new Coord(3, 0),
    ]);
    constructor(initialCoord) {
        super(1, HorizontalVictoryPattern.HORIZONTAL, initialCoord);
    }
    getAllPatterns(state) {
        const maxX = state.getWidth() - 4;
        return state
            .getCoordsAndContents()
            .map((coordAndContent) => coordAndContent.coord)
            .filter((coord) => coord.x <= maxX)
            .map((coord) => new HorizontalVictoryPattern(coord));
    }
}
class DescendingDiagonalVictoryPattern extends VictoryPattern {
    static DESCENDING_DIAGONAL = new CoordSet([
        new Coord(0, 0),
        new Coord(1, 1),
        new Coord(2, 2),
        new Coord(3, 3),
    ]);
    constructor(initialCoord) {
        super(1, DescendingDiagonalVictoryPattern.DESCENDING_DIAGONAL, initialCoord);
    }
    getAllPatterns(state) {
        const maxX = state.getWidth() - 4;
        const maxY = state.getHeight() - 4;
        return state
            .getCoordsAndContents()
            .map((coordAndContent) => coordAndContent.coord)
            .filter((coord) => coord.x <= maxX && coord.y <= maxY)
            .map((coord) => new DescendingDiagonalVictoryPattern(coord));
    }
}
class AscendingDiagonalVictoryPattern extends VictoryPattern {
    static ASCENDING_DIAGONAL = new CoordSet([
        new Coord(0, 0),
        new Coord(1, -1),
        new Coord(2, -2),
        new Coord(3, -3),
    ]);
    constructor(initialCoord) {
        super(1, AscendingDiagonalVictoryPattern.ASCENDING_DIAGONAL, initialCoord);
    }
    getAllPatterns(state) {
        const maxX = state.getWidth() - 4;
        const minY = 3;
        const maxY = state.getHeight() - 1;
        return state
            .getCoordsAndContents()
            .map((coordAndContent) => coordAndContent.coord)
            .filter((coord) => coord.x <= maxX && minY <= coord.y && coord.y <= maxY)
            .map((coord) => new AscendingDiagonalVictoryPattern(coord));
    }
}
class SquareVictoryPattern extends VictoryPattern {
    static SQUARE = new CoordSet([
        new Coord(0, 0),
        new Coord(0, 1),
        new Coord(1, 0),
        new Coord(1, 1),
    ]);
    constructor(initialCoord) {
        super(2, SquareVictoryPattern.SQUARE, initialCoord);
    }
    getAllPatterns(state) {
        const maxX = state.getWidth() - 2;
        const maxY = state.getHeight() - 2;
        return state
            .getCoordsAndContents()
            .map((coordAndContent) => coordAndContent.coord)
            .filter((coord) => coord.x <= maxX && coord.y <= maxY)
            .map((coord) => new SquareVictoryPattern(coord));
    }
}
export class QuartoRules extends ConfigurableRules {
    static singleton = MGPOptional.empty();
    static RULES_CONFIG_DESCRIPTION = new RulesConfigDescription({
        name: () => $localize `Quarto`,
        config: {
            playerZeroLevel: new NumberConfig(1, () => $localize `Player One Level`, MGPValidators.range(1, 2)),
            playerOneLevel: new NumberConfig(1, () => $localize `Player Two Level`, MGPValidators.range(1, 2)),
        },
    });
    static get() {
        if (QuartoRules.singleton.isAbsent()) {
            QuartoRules.singleton = MGPOptional.of(new QuartoRules());
        }
        return QuartoRules.singleton.get();
    }
    getRulesConfigDescription() {
        return QuartoRules.RULES_CONFIG_DESCRIPTION;
    }
    getInitialState(_config) {
        const board = TableUtils.create(4, 4, QuartoPiece.EMPTY);
        return new QuartoState(board, 0, QuartoPiece.AAAA);
    }
    isOccupied(square) {
        return (square !== QuartoPiece.EMPTY);
    }
    isLegal(move, state) {
        /**
         * pieceInHand is the one to be placed
         * move.piece is the one given to the next player
         */
        const x = move.coord.x;
        const y = move.coord.y;
        const pieceToGive = move.piece;
        const board = state.getCopiedBoard();
        const pieceInHand = state.pieceInHand;
        if (this.isOccupied(board[y][x])) {
            // we can't play on an occupied square
            return MGPValidation.failure(RulesFailure.MUST_LAND_ON_EMPTY_SPACE());
        }
        if (pieceToGive === QuartoPiece.EMPTY) {
            if (state.turn === 15) {
                // we must give a piece, except on the last turn
                return MGPValidation.SUCCESS;
            }
            return MGPValidation.failure(QuartoFailure.MUST_GIVE_A_PIECE());
        }
        if (QuartoState.isAlreadyOnBoard(pieceToGive, board)) {
            // the piece is already on the board
            return MGPValidation.failure(QuartoFailure.PIECE_ALREADY_ON_BOARD());
        }
        if (pieceInHand === pieceToGive) {
            // the piece given is the one in our hands, which is illegal
            return MGPValidation.failure(QuartoFailure.CANNOT_GIVE_PIECE_IN_HAND());
        }
        return MGPValidation.SUCCESS;
    }
    applyLegalMove(move, state, _config, _info) {
        const newBoard = state.getCopiedBoard();
        newBoard[move.coord.y][move.coord.x] = state.pieceInHand;
        const resultingState = new QuartoState(newBoard, state.turn + 1, move.piece);
        return resultingState;
    }
    updateBoardStatus(pattern, state, boardStatus) {
        if (boardStatus.status === AlignmentStatus.PRE_VICTORY) {
            if (this.isPatternVictorious(pattern, state)) {
                return {
                    boardStatus: {
                        status: AlignmentStatus.VICTORY,
                        sensitiveSquares: new CoordSet(),
                    },
                    isUpdated: true,
                };
            }
            else {
                return { boardStatus, isUpdated: false };
            }
        }
        else {
            return this.searchForVictoryOrPreVictoryInPattern(pattern, state, boardStatus);
        }
    }
    isPatternVictorious(pattern, state) {
        /**
         * if we found a pre-victory,
         * the only thing that can change the result is a victory
         */
        const initialCoord = new Coord(pattern.initialCoord.x, pattern.initialCoord.y);
        let c = state.getPieceAt(initialCoord);
        const commonCrit = new QuartoCriterion(c);
        for (const coord of pattern.getCoords()) {
            if (this.isOccupied(c) === false || commonCrit.areAllAbsent()) {
                break;
            }
            c = state.getPieceAt(coord);
            commonCrit.mergeWithQuartoPiece(c);
        }
        if (this.isOccupied(c) && commonCrit.areAllAbsent() === false) {
            /**
             * the last square was occupied, and there was some common criterion on all the four pieces
             * that's what victory is like in Quarto
             */
            return true;
        }
        else {
            return false;
        }
    }
    searchForVictoryOrPreVictoryInPattern(pattern, state, boardStatus) {
        // we're looking for a victory, pre-victory
        const patternInfos = this.getPatternInfos(pattern, state, boardStatus);
        if (patternInfos.boardStatus.isPresent()) {
            return { boardStatus: patternInfos.boardStatus.get(), isUpdated: false };
        }
        const commonCriterion = patternInfos.commonCriterion;
        const sensitiveCoord = patternInfos.sensitiveCoord;
        let isUpdated = false;
        // we now have looked through the entire pattern, we summarize everything
        if (commonCriterion.isPresent() && (commonCriterion.get().areAllAbsent() === false)) {
            // this pattern is not null and has a common criterion between all of its pieces
            if (sensitiveCoord.isAbsent()) {
                // the pattern is full
                return {
                    boardStatus: {
                        status: AlignmentStatus.VICTORY,
                        sensitiveSquares: new CoordSet(),
                    },
                    isUpdated: true,
                };
            }
            else {
                // if there is only one empty square, then the sensitive square we found is indeed sensitive
                if (commonCriterion.get().matchPiece(state.pieceInHand)) {
                    isUpdated = true;
                    const coord = sensitiveCoord.get();
                    boardStatus.sensitiveSquares = boardStatus.sensitiveSquares.addElement(coord);
                    boardStatus.status = AlignmentStatus.PRE_VICTORY;
                }
            }
        }
        return { boardStatus, isUpdated };
    }
    getPatternInfos(pattern, state, boardStatus) {
        let sensitiveCoord = MGPOptional.empty(); // the first square is empty
        let commonCriterion = MGPOptional.empty();
        const coords = pattern.getCoords();
        for (const coord of coords) {
            const c = state.getPieceAt(coord);
            // we look through the entire pattern
            if (c === QuartoPiece.EMPTY) {
                // if c is unoccupied
                if (sensitiveCoord.isAbsent()) {
                    sensitiveCoord = MGPOptional.of(coord);
                }
                else {
                    // 2 empty spaces: no victory or pre-victory, or new criterion
                    return {
                        sensitiveCoord: MGPOptional.of(coord),
                        commonCriterion,
                        boardStatus: MGPOptional.of(boardStatus),
                    };
                }
            }
            else {
                // if c is occupied
                if (commonCriterion.isAbsent()) {
                    commonCriterion = MGPOptional.of(new QuartoCriterion(c));
                    Debug.display('QuartoRules', 'getPatternInfos', 'set commonCrit to ' + commonCriterion.toString());
                }
                else {
                    commonCriterion.get().mergeWithQuartoPiece(c);
                    Debug.display('QuartoRules', 'getPatternInfos', 'update commonCrit: ' + commonCriterion.toString());
                }
            }
        }
        return { commonCriterion, sensitiveCoord, boardStatus: MGPOptional.empty() };
    }
    getGameStatus(node, config) {
        const state = node.gameState;
        let boardStatus = {
            status: AlignmentStatus.NOTHING,
            sensitiveSquares: new CoordSet(),
        };
        const maxLevel = Math.max(config.playerZeroLevel, config.playerOneLevel);
        const patterns = this.getPatterns(maxLevel, state);
        // at turn N, the previous opponent is the current player at turn N+1
        const opponent = state.getCurrentPlayer();
        const player = opponent.getOpponent();
        let playerMadeAVictory = false;
        for (const pattern of patterns) {
            const boardStatusUpdate = this.updateBoardStatus(pattern, state, boardStatus);
            boardStatus = boardStatusUpdate.boardStatus;
            if (boardStatusUpdate.isUpdated && boardStatus.status === AlignmentStatus.VICTORY) {
                if (this.isOnlyPlayerVictory(pattern, config, opponent)) {
                    return GameStatus.getVictory(opponent);
                }
                else if (this.isPlayerVictory(pattern, config, player)) {
                    playerMadeAVictory = true;
                }
            }
        }
        if (playerMadeAVictory) {
            return GameStatus.getVictory(player);
        }
        if (state.turn === 16) {
            return GameStatus.DRAW;
        }
        else {
            return GameStatus.ONGOING;
        }
    }
    isPlayerVictory(pattern, config, player) {
        if (player === Player.ZERO) {
            return pattern.level <= config.playerZeroLevel;
        }
        else {
            return pattern.level <= config.playerOneLevel;
        }
    }
    isOnlyPlayerVictory(pattern, config, player) {
        return this.isPlayerVictory(pattern, config, player) &&
            this.isPlayerVictory(pattern, config, player.getOpponent()) === false;
    }
    getPatterns(level, state) {
        const verticalInitialPattern = new VerticalVictoryPattern(new Coord(0, 0));
        const horizontalInitialPattern = new HorizontalVictoryPattern(new Coord(0, 0));
        const descendingDiagonalPattern = new DescendingDiagonalVictoryPattern(new Coord(0, 0));
        const ascendingDiagonalPattern = new AscendingDiagonalVictoryPattern(new Coord(0, 3));
        const initialPatterns = [
            verticalInitialPattern,
            horizontalInitialPattern,
            descendingDiagonalPattern,
            ascendingDiagonalPattern,
        ];
        if (level >= 2) {
            const squareInitialPattern = new SquareVictoryPattern(new Coord(0, 0));
            initialPatterns.push(squareInitialPattern);
        }
        return initialPatterns.flatMap((pattern) => pattern.getAllPatterns(state));
    }
    getVictoriousCoords(state, config) {
        const maxPatternLevel = Math.max(config.playerZeroLevel, config.playerOneLevel);
        const patterns = this.getPatterns(maxPatternLevel, state);
        for (const pattern of patterns) {
            if (this.isPatternVictorious(pattern, state)) {
                return pattern.getCoords();
            }
        }
        return new Set();
    }
}
